import React, { useState, useEffect } from 'react';
import { fetchProductAlerts } from '../services/api';
import { toTitleCase } from '../utils/format';

const PAGE_SIZE = 50;

// Ship dates arrive as a date or a date-time; read the calendar day as written so a
// midnight value does not slip back a day in Eastern time.
function fmtDay(d) {
  if (!d) return '—';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  const dt = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d);
  return isNaN(dt) ? String(d) : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ProductAlerts({ navigateTo }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    fetchProductAlerts(page, PAGE_SIZE)
      .then(d => { if (alive) setData(d); })
      .catch(e => { if (alive) setError(e.message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [page]);

  const rows = data?.data ?? [];
  const total = data?.totalRows ?? rows.length;
  const th = { textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Product Alerts</h2>
        <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4 }}>
          Orders with a product alert on them, newest first. Click an order to open it.
        </div>
      </div>

      <div className="card">
        <div className="card-header">Product Alerts {total > 0 && `(${total.toLocaleString()})`}</div>
        {data && !data.live && !loading && (
          <div style={{ padding: '8px 16px', fontSize: 12, color: '#7a5b00', background: '#fffaf0', borderBottom: '1px solid #f0c36d' }}>
            The live list could not be reached, so this is CPx's own copy. It shows the alert's number, not its name.
          </div>
        )}
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No product alerts.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ ...th, padding: '8px 12px' }}>Order ID</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Alert</th>
                    <th style={{ ...th, textAlign: 'right', padding: '8px 12px' }}>Ship Date</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.orderId ?? i}
                      onClick={() => navigateTo?.('order-details', String(row.bcOrderId || row.orderId))}
                      style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: navigateTo ? 'pointer' : 'default' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{row.bcOrderId || row.orderId || '—'}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase(row.billTo || '—')}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-700)', fontWeight: 600 }}>{row.alert || '—'}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{fmtDay(row.shipDate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 12 }}>← Prev</button>
                <span style={{ fontSize: 12, alignSelf: 'center' }}>Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={page * PAGE_SIZE >= total} className="btn btn-navy" style={{ fontSize: 12 }}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
